import React, { memo } from "react";
import {
  Modal as RNModal,
  View,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ScrollView,
} from "react-native";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { Feather } from "@expo/vector-icons";
import { Icon } from "@/components/ui/Icon";
import { cn } from "@/lib/utils";
import { KeyboardAvoidingView } from "./KeyboardAvoidingView";

export interface ModalProps {
  visible: boolean;
  onDismiss: () => void;
  children: React.ReactNode;
  title?: TranslationKeys | null;
  subtitle?: TranslationKeys | null;
  /** When false the modal cannot be dismissed by tapping the backdrop (default: true). */
  closeOnBackdrop?: boolean;
  /** Forwarded to the outer modal container for testing. */
  testID?: string;
  /** Animation type for modal appearance/disappearance (default: "fade"). */
  animationType?: "none" | "slide" | "fade";
  footerContainerClassName?: string; // Optional className for the footer container
  footer?: React.ReactNode;
  useScrollView?: boolean; // For modals with long content that may exceed screen height
  scrollViewProps?: React.ComponentProps<typeof ScrollView>; // Optional props for the ScrollView
  contentContainerProps?: React.ComponentProps<typeof View>; // Optional props for the content container View
  contentProps?: React.ComponentProps<typeof View>; // Optional props for the content container View
}

export const Modal = memo(function Modal({
  visible,
  onDismiss,
  children,
  title,
  subtitle,
  closeOnBackdrop = true,
  animationType = "fade",
  testID,
  footer,
  footerContainerClassName,
  useScrollView = true,
  contentProps,
  scrollViewProps,
  contentContainerProps,
  ...rest
}: ModalProps) {
  const { t, isRTL } = useI18n();
  const resolvedTitle = title ? t(title) : undefined;
  const resolvedSubtitle = subtitle ? t(subtitle) : undefined;

  return (
    <RNModal
      visible={visible}
      transparent
      animationType={animationType}
      statusBarTranslucent
      onRequestClose={onDismiss}
      {...rest}
    >
      {/* Backdrop */}
      <TouchableWithoutFeedback
        onPress={closeOnBackdrop ? onDismiss : undefined}
        accessible={false}
      >
        <View className="flex-1 bg-black/50 items-center justify-center px-6">
          {/* KeyboardAvoidingView prevents the modal from being covered by the keyboard */}
          <KeyboardAvoidingView className="w-full items-center" behavior="translate-with-padding">
            {/* Stop backdrop tap from propagating into modal content */}
            <TouchableWithoutFeedback accessible={false}>
              <View
                testID={testID}
                {...contentContainerProps}
                className={cn(
                  "bg-white rounded-xl w-full max-w-xl max-h-[85vh] overflow-hidden shadow-xl",
                  contentContainerProps?.className
                )}
                /* Web a11y: aria-modal scopes focus to this region */
                aria-modal
                accessible
                accessibilityViewIsModal
              >
                {/* Header row — flips in RTL */}
                <View
                  className={cn(
                    "flex-row items-center justify-between px-4 lg:px-6 py-4 lg:py-6",
                    !!resolvedTitle && "border-b border-stroke-50"
                  )}
                >
                  <View>
                    {!!resolvedTitle && (
                      <AppText weight="semibold" className="text-lg text-dark-text-50">
                        {resolvedTitle}
                      </AppText>
                    )}
                    {!!resolvedSubtitle && (
                      <AppText weight="medium" className="text-sm text-supporting-text">
                        {resolvedSubtitle}
                      </AppText>
                    )}
                  </View>
                  <TouchableOpacity
                    onPress={onDismiss}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    accessibilityRole="button"
                    accessibilityLabel={t("common.close")}
                    className={isRTL ? "me-0 ms-2" : "ms-0 me-0"}
                  >
                    <Icon as={Feather} name="x" size={20} className="text-dark-text-50" />
                  </TouchableOpacity>
                </View>

                {useScrollView ? (
                  <ScrollView
                    {...scrollViewProps}
                    showsVerticalScrollIndicator={false}
                    contentContainerClassName={cn(
                      "px-4 lg:px-6 py-4 lg:py-6 flex-grow-1",
                      scrollViewProps?.contentContainerClassName
                    )}
                  >
                    {children}
                  </ScrollView>
                ) : (
                  <View
                    {...contentProps}
                    className={cn("px-4 lg:px-6 py-4 lg:py-6", contentProps?.className)}
                  >
                    {children}
                  </View>
                )}

                {footer && (
                  <View
                    className={cn("p-4 lg:p-6 border-t border-stroke-50", footerContainerClassName)}
                  >
                    {footer}
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          </KeyboardAvoidingView>
        </View>
      </TouchableWithoutFeedback>
    </RNModal>
  );
});
